import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';

const EmployeeManagement = () => {
  const { user, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPassword, setEditingPassword] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'employee',
    full_name: ''
  });
  
  // Password update state
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (isAdmin()) {
      fetchEmployees();
    }
  }, [isAdmin]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setMessage(''); // Clear any previous messages
      
      console.log('Fetching employees...');
      const cacheBuster = Date.now();
      
      // Use apiRequest which handles auth headers automatically
      const response = await apiRequest(`/admin/employees?ts=${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setMessage('Error fetching employees: HTTP ' + response.status);
        setEmployees([]);
        setLoading(false);
        return;
      }
      
      let data;
      try {
        const text = await response.text();
        console.log('Response text:', text.substring(0, 200)); // Log first 200 chars
        data = text ? JSON.parse(text) : {};
        console.log('Parsed data:', data);
      } catch (e) {
        console.error('Parse error:', e);
        setMessage('Error parsing response: ' + e.message);
        setEmployees([]);
        setLoading(false);
        return;
      }
      
      if (data.success) {
        const employeesList = data.employees || [];
        console.log('Fetched employees:', employeesList.length, 'employees');
        console.log('Employee list:', employeesList);
        setEmployees(employeesList);
        setMessage(''); // Clear any error messages on success
      } else {
        console.error('API returned error:', data.error || data.message);
        setMessage('Error fetching employees: ' + (data.error || data.message || 'Unknown error'));
        setEmployees([]);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setMessage('Network error: ' + err.message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    if (!formData.email.includes('@')) {
      setMessage('Email must include @ symbol (e.g., employee@menthing)');
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await apiRequest('/admin/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        setMessage('Error parsing response: ' + e.message);
        setActionLoading(false);
        return;
      }

      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data.error) {
          errorMsg = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
        }
        setMessage('Error creating employee: ' + errorMsg);
        setActionLoading(false);
        return;
      }

      if (data.success) {
        const createdEmployee = data.employee;
        console.log('Employee created successfully:', createdEmployee);
        setMessage('Employee created successfully!');
        setShowCreateForm(false);
        setFormData({
          username: '',
          email: '',
          password: '',
          role: 'employee',
          full_name: ''
        });
        // Immediately add the new employee to the list (optimistic update)
        if (createdEmployee) {
          setEmployees(prev => {
            const exists = prev.some(emp => emp.id === createdEmployee.id || emp.username === createdEmployee.username);
            if (!exists) {
              console.log('Adding new employee to list (optimistic update):', createdEmployee);
              // Add with proper structure matching what the API returns
              const newEmployee = {
                id: createdEmployee.id,
                username: createdEmployee.username,
                email: createdEmployee.email,
                role: createdEmployee.role || 'employee',
                company: createdEmployee.company || 'menthing',
                full_name: createdEmployee.full_name || '',
                is_active: 1,
                created_at: new Date().toISOString()
              };
              return [newEmployee, ...prev];
            }
            console.log('Employee already in list, skipping optimistic update');
            return prev;
          });
        }
        // Force refresh from server multiple times to ensure we get the latest data
        console.log('Employee created, refreshing list from server...');
        // Immediate refresh
        await fetchEmployees();
        // Refresh again after 500ms
        setTimeout(async () => {
          console.log('Second refresh from server...');
          await fetchEmployees();
        }, 500);
        // Final refresh after 2 seconds
        setTimeout(async () => {
          console.log('Final refresh from server...');
          await fetchEmployees();
        }, 2000);
      } else {
        setMessage('Error creating employee: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
      console.error('Error creating employee:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePassword = async (employeeId) => {
    if (!newPassword || newPassword.length < 6) {
      setMessage('Password must be at least 6 characters');
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiRequest(`/admin/employees/${employeeId}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        setMessage('Error parsing response: ' + e.message);
        setActionLoading(false);
        return;
      }

      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data.error) {
          errorMsg = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
        }
        setMessage('Error updating password: ' + errorMsg);
        setActionLoading(false);
        return;
      }

      if (data.success) {
        setMessage('Password updated successfully!');
        setEditingPassword(null);
        setNewPassword('');
      } else {
        setMessage('Error updating password: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
      console.error('Error updating password:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEmployee = async (employeeId, username, email) => {
    if (!window.confirm(`Are you sure you want to delete employee "${username}"?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const qs = new URLSearchParams();
      if (username) qs.append('username', username);
      if (email) qs.append('email', email);
      const response = await apiRequest(`/admin/employees/${employeeId}?${qs.toString()}`, {
        method: 'DELETE'
      });

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        setMessage('Error parsing response: ' + e.message);
        setActionLoading(false);
        return;
      }

      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        if (data.error) {
          errorMsg = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
        }
        setMessage('Error deleting employee: ' + errorMsg);
        setActionLoading(false);
        return;
      }

      if (data.success) {
        setMessage('Employee deleted successfully!');
        // Immediately refresh employee list
        console.log('Employee deleted, refreshing list...');
        await fetchEmployees();
      } else {
        setMessage('Error deleting employee: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      setMessage('Network error: ' + err.message);
      console.error('Error deleting employee:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return dateString;
    }
  };

  if (!isAdmin()) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
        <p className="text-gray-600">You must be an admin to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Employee Management</h2>
            <p className="text-gray-600 mt-1">Manage employee accounts and passwords</p>
            {employees.length > 0 && (
              <p className="text-sm text-gray-500 mt-1">Total: {employees.length} employees</p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchEmployees}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh employee list"
            >
              {loading ? 'Loading...' : '🔄 Refresh'}
            </button>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showCreateForm ? 'Cancel' : '+ Add New Employee'}
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('successfully') || message.includes('Success')
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Create Employee Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Create New Employee</h3>
          <form onSubmit={handleCreateEmployee} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="username"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (with @company) *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="employee@menthing"
                />
                <p className="text-xs text-gray-500 mt-1">Format: username@company (e.g., john@menthing)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password (min 6 chars) *
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter password"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name (Optional)
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Doe"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({
                    username: '',
                    email: '',
                    password: '',
                    role: 'employee',
                    full_name: ''
                  });
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Creating...' : 'Create Employee'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Employees List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            All Employees ({employees.length})
          </h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading employees...</div>
        ) : employees.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No employees found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.map((emp) => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{emp.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{emp.email || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {emp.company || 'menthing'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        emp.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {emp.role || 'employee'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{emp.full_name || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(emp.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {editingPassword === emp.id ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="New password"
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                              minLength={6}
                            />
                            <button
                              onClick={() => handleUpdatePassword(emp.id)}
                              disabled={actionLoading || !newPassword || newPassword.length < 6}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => {
                                setEditingPassword(null);
                                setNewPassword('');
                              }}
                              className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setEditingPassword(emp.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Change Password
                            </button>
                            {emp.role !== 'admin' && (
                              <button
                                onClick={() => handleDeleteEmployee(emp.id, emp.username, emp.email)}
                                disabled={actionLoading}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeManagement;

